import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  Pipe,
  PipeTransform,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  Observable,
  Subscription as RxSubscription,
  filter,
  map,
  of,
  startWith,
  take,
  tap,
  zip,
} from 'rxjs';
import { Subscription } from 'src/app/shared/subscription/subscription.models';
import { PermissionsService } from '../../auth/permissions/permissions.service';
import { selectSubscription } from '../../shared/subscription/store/subscription.selectors';
import { Vehicle } from '../../shared/vehicle/models/vehicle.class';
import { selectVehicleList } from '../../shared/vehicle/store/vehicle.selectors';
import { NavigationElement } from './sidenav.models';
import { SidenavService } from './sidenav.service';
import { DEFAULT_NAVIGATION, EXTERNAL_LINKS, GET_STARTED_NAVIGATION } from './sidenav.static';

/**
 * Sidenav domestic Pipe that checks if a NavigationElement is currently active
 * by comparing the `link` member of the element with the current Router URL
 */
@Pipe({
  name: 'isActive',
})
export class IsPageActivePipe implements PipeTransform {
  constructor(private readonly router: Router) {}

  transform(page: NavigationElement): Observable<boolean> {
    if (!page) return of(false);

    return this.router.events.pipe(
      // listen to router events emitted at the navigation end only
      filter((routerEvent) => routerEvent instanceof NavigationEnd),
      // start with the current router state,
      // since first NavigationEnd event emits before this gets called on the very first time
      startWith(this.router),
      map(() => {
        return (
          // check if the page is active
          this.router.url.split('?')[0] === page.link ||
          // or if the page has children and one of those children is active
          (page.subPages !== undefined &&
            page.subPages.length > 0 &&
            page.subPages.some((subPage) => this.router.url.split('?')[0] === subPage.link))
        );
      })
    );
  }
}

@Component({
  selector: 'app-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
  providers: [IsPageActivePipe],
})
export class SidenavComponent implements OnInit, OnDestroy {
  private readonly _rxSubscription = new RxSubscription();

  navigationPages = DEFAULT_NAVIGATION;
  getStartedPages = GET_STARTED_NAVIGATION;
  externalLinks = EXTERNAL_LINKS;

  hasVehicles = true;
  getStartedClicked = false;

  subscription!: Subscription;

  activeSubMenus = new Set<string>();

  constructor(
    private readonly changeDetector: ChangeDetectorRef,
    private readonly router: Router,
    private readonly store: Store,
    private readonly isActive: IsPageActivePipe,
    private readonly permissionService: PermissionsService,
    public readonly sidenav: SidenavService,
    public readonly dialog: MatDialog
  ) {}

  ngOnInit(): void {
    // Check if any sub-menu should be opened on element init
    for (const page of this.navigationPages) {
      if (page.subPages !== undefined) {
        this.isActive
          .transform(page)
          .pipe(
            take(1),
            tap((isActive) => {
              if (isActive) this.activeSubMenus.add(page.name);
            })
          )
          .subscribe();
      }
    }

    const subscription$ = this.store
      .select(selectSubscription)
      .pipe(tap((subscription) => (this.subscription = subscription.value!)));
    this._rxSubscription.add(subscription$.subscribe());

    const vehicleList$ = this.store.select(selectVehicleList).pipe(
      filter(
        (vehicles: Vehicle[] | null | undefined) => vehicles !== undefined && vehicles !== null
      ),
      tap((vehicles: Vehicle[] | null | undefined) => {
        if (!vehicles || (vehicles && vehicles.length < 1)) this.hasVehicles = false;
        else this.hasVehicles = true;
        this.changeDetector.markForCheck();
      })
    );
    this._rxSubscription.add(vehicleList$.subscribe());

    if (this.router.url.includes('setup-wizard')) this.getStartedClicked = true;
    else this.getStartedClicked = false;
    this.changeDetector.markForCheck();
  }

  ngOnDestroy(): void {
    this._rxSubscription.unsubscribe();
  }

  handleGetStartedClick() {
    void this.router.navigate(['admin', 'setup-wizard']);
  }

  showParentPage(page: NavigationElement): Observable<boolean> {
    if (page.subPages !== undefined && page.subPages.length > 0) {
      if (page.subPages.some((curPage) => curPage.name === 'Profile')) return of(true);

      const observables = page.subPages.map((subPage) =>
        this.permissionService.hasPermissions(subPage.permissionsRequired ?? [])
      );
      return zip(observables).pipe(
        map((permissionsArray: boolean[]) =>
          permissionsArray.some((hasPermission) => hasPermission)
        )
      );
    }
    return of(true);
  }

  toggleSubMenu(page: NavigationElement): void {
    if (this.activeSubMenus.has(page.name)) this.activeSubMenus.delete(page.name);
    else this.activeSubMenus.add(page.name);

    this.activeSubMenus = new Set(this.activeSubMenus);
  }

  onSmallerItemClick(action: (dialog: MatDialog) => void): void {
    action(this.dialog);
  }
}
